import { createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";
import { errorHandler } from "../../../helpers/errorHandler";
import {
    Call,
    CallWithFormDataFile,
    downloadBlob,
} from "../../../helpers/fetch";
import {
    startUILoading,
    stopUILoading,
} from "../../actions/administrador/ui/ui";
import { deleteConfirmModal } from "@helpers/DeleteValidate";

const initialState = {
    pagination: {
        data: [],
        per_page: 25,
        total: 0,
    },
    studentsExamsActives: [],
    studentsExamsAssignments: null,
    studentsUnansweredQuestions: [],
    examAssignmentStatus: "PENDIENTE",
};

export const Exams = createSlice({
    name: "Exams",
    initialState,
    reducers: {
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        clearPagination: (state, action) => {
            state.pagination = initialState.pagination;
        },
        setStudentsExamsActives: (state, action) => {
            state.studentsExamsActives = action.payload;
        },
        clearStudentsExamsActives: (state) => {
            state.studentsExamsActives = initialState.studentsExamsActives;
        },
        setStudentsExamAssignment: (state, action) => {
            state.studentsExamsAssignments = action.payload;
        },
        clearStudentsExamAssignment: (state) => {
            state.studentsExamsAssignments =
                initialState.studentsExamsAssignments;
            state.examAssignmentStatus = initialState.examAssignmentStatus;
        },
        setStudentAnswer: (state, action) => {
            const questions = state.studentsExamsAssignments?.survey?.fields;
            const question = questions?.find(
                (q) => q.id === action.payload.survey_field_id
            );
            if (question) {
                question.answer = action.payload;
            }
        },
        setStudentsUnansweredQuestions: (state, action) => {
            state.studentsUnansweredQuestions = action.payload;
        },
        removeStudentsUnansweredQuestion: (state, action) => {
            state.studentsUnansweredQuestions =
                state.studentsUnansweredQuestions.filter(
                    (id) => id !== action.payload
                );
        },
        clearStudentsUnansweredQuestions: (state) => {
            state.studentsUnansweredQuestions =
                initialState.studentsUnansweredQuestions;
        },
        setExamAssignmentStatus: (state, action) => {
            state.examAssignmentStatus = action.payload;
        },
        clearExamAssignmentStatus: (state) => {
            state.examAssignmentStatus = initialState.examAssignmentStatus;
        },
        clearExamsState: () => initialState,
    },
});

export const {
    setPagination,
    clearPagination,
    setStudentsExamsActives,
    clearStudentsExamsActives,
    setStudentsExamAssignment,
    clearStudentsExamAssignment,
    setStudentAnswer,
    setStudentsUnansweredQuestions,
    removeStudentsUnansweredQuestion,
    clearStudentsUnansweredQuestions,
    setExamAssignmentStatus,
    clearExamAssignmentStatus,
    clearExamsState,
} = Exams.actions;

export default Exams.reducer;

//* ACTIONS

export const startGetActiveExams = () => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call("students/exams/actives", "GET");

        if (resp.success) {
            dispatch(setStudentsExamsActives(resp.success));
        }

        dispatch(stopUILoading());
    };
};

export const startGetExamAssignmentByStudent = (
    exam_assignment_id,
    navigate = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `students/exams/assignment/${exam_assignment_id}`,
            "GET"
        );

        if (resp.success) {
            dispatch(setStudentsExamAssignment(resp.success));
        } else {
            if (resp?.error?.expired) {
                Swal.fire({
                    title: "Examen expirado",
                    text: resp?.error?.msg,
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                }).then(() => {
                    navigate("/my/surveys");
                });
            } else if (resp?.error?.unavailable) {
                Swal.fire({
                    title: "Examen no disponible",
                    text: resp?.error?.msg,
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                }).then(() => {
                    navigate("/my/surveys");
                });
            } else {
                errorHandler(resp);
            }
        }

        dispatch(stopUILoading());
    };
};

export const startGetExamAssignment = (
    exam_assignment_id,
    navigate = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            `operators/exams/assignment/by-id/${exam_assignment_id}`,
            "GET"
        );

        if (resp.success) {
            dispatch(setStudentsExamAssignment(resp.success));
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startAnswerExam = (
    data,
    navigate = () => {},
    callback = () => {},
    fail = () => {}
) => {
    return async (dispatch) => {
        const { exam_assignment_id, survey_field_id } = data;

        const resp = await Call(
            `students/exams/assignment/answer/${exam_assignment_id}/${survey_field_id}`,
            "POST",
            data
        );

        if (resp.success) {
            dispatch(setStudentAnswer(resp.success));
            callback();
        } else {
            if (resp?.error?.expired) {
                Swal.fire({
                    title: "Examen expirado",
                    text: "El tiempo para responder el examen ha terminado. Tu examen ha sido finalizado automáticamente.",
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                }).then(() => {
                    navigate("/my/surveys");
                });
            } else {
                errorHandler(resp);
                fail();
            }
        }
    };
};

export const startEndExam = (exam_assignment_id, navigate = () => {}) => {
    return async (dispatch) => {
        const resp = await Call(
            `students/exams/assignment/end/${exam_assignment_id}`,
            "POST"
        );

        if (resp.success) {
            Swal.fire({
                title: "Examen finalizado",
                text: "Has finalizado el examen exitosamente.",
                icon: "success",
                confirmButtonText: "Aceptar",
            }).then(() => {
                navigate("/my/surveys");
            });
        } else {
            errorHandler(resp);
        }
    };
};

//* ACTIONS OPERATORS

export const startShowExamsApplications = (values) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const {
            page,
            number_rows,
            survey_id,
            query,
            cycles,
            curriculum_id,
            status,
            started,
            is_export,
        } = values;

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("per_page", number_rows);
        params.set("q", query);
        params.set("with_stats", 1);
        if (survey_id) {
            params.set("survey_id", survey_id);
        }
        if (cycles) {
            params.set("cycles", `${cycles.year}-${cycles.sub_cycle}`);
        }
        if (curriculum_id.length > 0) {
            params.set(
                "curriculum_id",
                curriculum_id.map((c) => c.value).join(",")
            );
        }
        if (status) {
            params.set("status", status);
        }
        if (started.from) {
            params.set("started_from", started.from);
        }
        if (started.to) {
            params.set("started_to", started.to);
        }
        if (is_export) {
            params.set("export", 1);
        }

        if (is_export) {
            const resp = await CallWithFormDataFile(
                "operators/exams/application",
                "GET",
                params.toString()
            );
            downloadBlob(resp, "Resultados de examen.xlsx");
        } else {
            const resp = await Call(
                "operators/exams/application",
                "GET",
                params.toString()
            );
            if (resp.success) {
                dispatch(setPagination(resp.success.pagination));
            } else {
                errorHandler(resp);
            }
        }

        dispatch(stopUILoading());
    };
};

export const startLinkToService = (values, callback = () => {}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const resp = await Call(
            "operators/exams/assignment/link-service",
            "POST",
            values
        );

        if (resp.success) {
            Swal.fire({
                title: "Éxito",
                text: "Se ha vinculado el examen con el servicio exitosamente.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });
            callback();
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startUpdateApplicationPeriod = (
    exam_assignment_id,
    period,
    callback = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const { from: begins_at, to: expires_at } = period;

        const resp = await Call(
            `operators/exams/assignment/update-period/${exam_assignment_id}`,
            "PATCH",
            {
                begins_at,
                expires_at,
            }
        );

        if (resp.success) {
            Swal.fire({
                title: "Éxito",
                text: "Se ha actualizado el periodo de aplicación del examen exitosamente.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });
            callback();
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startDelete = (exam_assignment_id, callback = () => {}) => {
    return async (dispatch) => {
        await deleteConfirmModal(async () => {
            dispatch(startUILoading());
            const resp = await Call(
                `operators/exams/assignment/${exam_assignment_id}`,
                "DELETE"
            );

            if (resp.success) {
                callback();
                Swal.fire({
                    title: "Éxito",
                    text: "Examen eliminado correctamente.",
                    icon: "success",
                    toast: true,
                    position: "bottom-end",
                    timer: 8000,
                });
            } else {
                errorHandler(resp);
            }
            dispatch(stopUILoading());
        });
    };
};
